// @flow
import React, { Component, Fragment } from 'react';
import type { Node } from 'react';
import PropTypes from 'prop-types';

// Helpers
import classNames from 'classnames';
import { gtmEventPush } from 'utils/gtm/events';
import { formatExternalLink } from 'utils/utmLinks';

import Icon from 'Common/Icon';

import styles from 'components/CardVertical.scss';
import Picture from 'Common/Picture';
import Image from 'Common/Image';

import Button from 'Common/Button';
import isWindow from 'Common/isWindow';
import parsedPrice from 'Common/parsedPrice';

/**
 * <Card /> component.
 * @return {class} returns card instance
 */
class CardVertical extends Component<*, *> {
  /**
   * @param {object} props - props passed
   */
  constructor(props: Object) {
    super(props);
    this.state = {
      imageSrc: this.props.image,
      showDealerImage: true
    };
  }

  /**
   * Implements propTypes().
   *
   * @return {Object} - propType object.
   * @prop {string} id - card id.
   * @prop {string} name - card name.
   * @prop {number} price - card price.
   * @prop {string} year - card year.
   * @prop {oneOfType} distanceOrTown - card distanceOrTown.
   * @prop {number} mileage - card mileage.
   * @prop {string} fuelType - card fuelType.
   * @prop {number} doors - card doors.
   * @prop {string} transmission - card transmission.
   * @prop {string} image - card image.
   * @prop {object} history - history.
   * @prop {object} item - item.
   * @prop {func} handleClick: handleClick
   */
  static propTypes = {
    id: PropTypes.string,
    isLeasingPage: PropTypes.bool,
    name: PropTypes.string,
    make: PropTypes.string,
    range: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    year: PropTypes.string,
    distanceOrTown: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    mileage: PropTypes.number,
    fuelType: PropTypes.string,
    doors: PropTypes.number,
    transmission: PropTypes.string,
    url: PropTypes.string,
    image: PropTypes.object,
    dealerImage: PropTypes.string,
    cardLayout: PropTypes.string,
    history: PropTypes.object,
    item: PropTypes.object,
    handleClick: PropTypes.func,
    network: PropTypes.string,
    gcLid: PropTypes.string,
    gtmSectionName: PropTypes.string.isRequired,
    dataBi: PropTypes.string,
    dataSection: PropTypes.string,
    productId: PropTypes.string,
    model: PropTypes.string,
    generation: PropTypes.string,
    derivative: PropTypes.string,
    trim: PropTypes.string,
    bodyType: PropTypes.string,
    utm: PropTypes.string
  };

  /**
   * Implements defaultProps().
   */
  static defaultProps: Object = {
    cardLayout: 'horizontal'
  };

  setFallbackDealerLogo: () => void = () => {
    this.setState({
      showDealerImage: false
    });
  };

  updatedRoute: (path: string) => void = path => {
    this.props.history.push(path);
  };

  handleClick: (e: SyntheticEvent<HTMLButtonElement>) => void = e => {
    const { item, handleClick } = this.props;
    e.preventDefault();
    handleClick(item);
    const url = this.generateUrl();
    isWindow && window.open(url, '_blank');
  };

  handleParentClick: (
    e: SyntheticEvent<HTMLButtonElement>,
    name: string
  ) => void = (e, name) => {
    gtmEventPush({
      method: this.props.gtmSectionName,
      args: {
        itemLabel: `Popular cars - ${name}`
      }
    });
    this.handleClick(e);
  };

  handleClickChild: (
    e: SyntheticEvent<HTMLButtonElement>,
    name: string
  ) => void = (e, name) => {
    // Stop the parent onClick from happening too.
    e.stopPropagation();

    gtmEventPush({
      method: this.props.gtmSectionName,
      args: {
        itemLabel: `Popular cars - ${name}`
      }
    });
    this.handleClick(e);
  };

  generateUrl: () => string = () => {
    const { network, gcLid, url, utm } = this.props;

    const linkParams = {
      link: url,
      network,
      gcLid
    };
    const uri = formatExternalLink(linkParams, utm);
    return uri;
  };

  /**
   * Renders <Card /> component.
   *
   * @return {JSX} - renders a logo.
   */
  render(): Node {
    const {
      name,
      price,
      year,
      distanceOrTown,
      mileage,
      fuelType,
      doors,
      transmission,
      dealerImage,
      cardLayout,
      image,
      dataBi,
      dataSection,
      productId,
      make,
      model,
      generation,
      derivative,
      trim,
      bodyType,
      range,
      item,
      isLeasingPage
    } = this.props;

    const url = this.generateUrl();
    const { card, classCardVertical } = styles;

    const personalPrice =
      item &&
      item.leasingData &&
      item.leasingData.monthlyPayment &&
      item.leasingData.monthlyPayment.personal;
    const personalLeasePrice = isLeasingPage ? parsedPrice(personalPrice) : '';

    const personalInitalPayment =
      item &&
      item.leasingData &&
      item.leasingData.initialPayment &&
      item.leasingData.initialPayment.personal;
    const personalInitalPrice = isLeasingPage
      ? parsedPrice(personalInitalPayment)
      : '';

    const termMonths = item && item.leasingData && item.leasingData.termMonths;
    const totalMileage =
      item && item.leasingData && item.leasingData.totalMileage;
    return (
      <a
        href={url}
        onClick={e => this.handleParentClick(e, name)}
        rel="nofollow"
        className={classNames([
          cardLayout === 'vertical' ? classCardVertical : card
        ])}
        data-bi={dataBi}
        data-section={dataSection}
        product-id={productId}
        make={make}
        model={model}
        generation={generation}
        derivative={derivative}
        trim={trim}
        bodyType={bodyType}
        fuelType={fuelType}
        range={range}
        transmission={transmission}
      >
        {image && (
          <div>
            <Picture
              pictures={{
                ...image,
                title: name
              }}
            />
          </div>
        )}
        {!image && (
          <div>
            <Image
              src={`${process.env.PUBLIC_URL || ''}/images/fallback.png`}
              alt={'Image unavailable'}
              title={'Image unavailable'}
            />
          </div>
        )}
        <div>
          <h3 className={styles.name}>{name}</h3>
          <div className={styles.horizontalDetails}>
            <div className={styles.detailsWrapper}>
              <div className={styles.price}>{price}</div>
              <Button
                extendStyle={styles.button}
                rel="nofollow"
                buttonText={isLeasingPage ? 'View deal' : 'View details'}
                onClick={e => this.handleClickChild(e, name)}
              />
            </div>
            <div className={styles.detailsWrapper}>
              <div className={styles.year}>
                <Icon
                  className={classNames(styles.icon)}
                  iconName={'calendar'}
                  size={'medium'}
                />
                {year}
              </div>
              {!!distanceOrTown && (
                <div
                  className={classNames(
                    styles.detail,
                    styles.detailDistanceOrTown
                  )}
                >
                  <Icon
                    className={classNames(
                      styles.icon,
                      styles.iconDistanceOrTown
                    )}
                    iconName={'location'}
                    size={'medium'}
                  />
                  {distanceOrTown}
                </div>
              )}
            </div>
            <div>
              <div className={styles.detailWrapper}>
                <div className={styles.detail} data-mileage>
                  <Icon
                    className={styles.icon}
                    iconName={'road'}
                    size={'medium'}
                  />
                  {mileage && mileage.toLocaleString('en')} Miles
                </div>

                {fuelType && (
                  <div className={styles.detail} data-fuel-type>
                    <Icon
                      className={styles.icon}
                      iconName={'fuel-tank'}
                      size={'medium'}
                    />
                    {fuelType}
                  </div>
                )}
                {transmission && (
                  <div className={styles.detail} data-transmission>
                    <Icon
                      className={styles.icon}
                      iconName={'gearbox'}
                      size={'medium'}
                    />
                    {transmission !== 'N/A'
                      ? transmission.toLowerCase()
                      : transmission}
                  </div>
                )}
                {doors && (
                  <div className={styles.detail} data-doors>
                    <Icon
                      className={styles.icon}
                      iconName={'door'}
                      size={'medium'}
                    />
                    {doors} Doors
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={styles.details}>
            <div>
              {!isLeasingPage ? (
                <Fragment>
                  <div className={styles.year}>
                    <Icon
                      className={classNames(styles.icon)}
                      iconName={'calendar'}
                      size={'medium'}
                    />
                    {year}
                  </div>
                  {price && <div className={styles.price}>{price}</div>}
                </Fragment>
              ) : (
                <Fragment>
                  <div className={styles.personalLeasePrice}>
                    <div>From: </div>
                    <div>
                      {personalLeasePrice && (
                        <div className={styles.price}>{personalLeasePrice}</div>
                      )}
                      <div>{'Per month (inc. VAT)'}</div>
                    </div>
                  </div>
                </Fragment>
              )}
              {isLeasingPage && (
                <p className={styles.payments}>
                  <strong>{personalInitalPrice}</strong> initial payment.{' '}
                  <strong>{termMonths}</strong> month contract.{' '}
                  <strong>{totalMileage}</strong> miles p/a. Subject to status
                  and conditions
                </p>
              )}
              {!!distanceOrTown && !isLeasingPage && (
                <div
                  className={classNames(
                    styles.detail,
                    styles.detailDistanceOrTownMobile
                  )}
                  data-distance-or-town
                >
                  <Icon
                    className={classNames(
                      styles.icon,
                      styles.iconDistanceOrTown
                    )}
                    iconName={'location'}
                    size={'medium'}
                  />
                  {typeof distanceOrTown === 'string'
                    ? distanceOrTown
                    : distanceOrTown}
                </div>
              )}
              <div className={styles.detailWrapper}>
                {!isLeasingPage && (
                  <div className={styles.detail} data-mileage>
                    <Icon
                      className={styles.icon}
                      iconName={'road'}
                      size={'medium'}
                    />
                    {mileage && mileage.toLocaleString('en')} Miles
                  </div>
                )}

                {fuelType && (
                  <div className={styles.detail} data-fuel-type>
                    <Icon
                      className={styles.icon}
                      iconName={'fuel-tank'}
                      size={'medium'}
                    />
                    {fuelType}
                  </div>
                )}
                {transmission && (
                  <div className={styles.detail} data-transmission>
                    <Icon
                      className={styles.icon}
                      iconName={'gearbox'}
                      size={'medium'}
                    />
                    {transmission !== 'N/A'
                      ? transmission.toLowerCase()
                      : transmission}
                  </div>
                )}
                {!isLeasingPage && doors && (
                  <div className={styles.detail} data-doors>
                    <Icon
                      className={styles.icon}
                      iconName={'door'}
                      size={'medium'}
                    />
                    {doors} Doors
                  </div>
                )}
              </div>
            </div>
            <div>
              <Icon iconName="open-window" />
              <Button
                extendStyle={styles.button}
                onClick={e => this.handleClickChild(e, name)}
                rel="nofollow"
                buttonText={isLeasingPage ? 'View deal' : 'View details'}
                buttonSuffix={
                  <Icon iconName="open-window" extendStyle={styles.icon} />
                }
              />
              {!!distanceOrTown && (
                <div
                  className={classNames(
                    styles.detail,
                    styles.detailDistanceOrTown
                  )}
                >
                  <Icon
                    className={classNames(
                      styles.icon,
                      styles.iconDistanceOrTown
                    )}
                    iconName={'location'}
                    size={'medium'}
                  />
                  {distanceOrTown}
                </div>
              )}
              {this.state.showDealerImage && dealerImage && (
                <div className={styles.logoWrapper}>
                  <img
                    className={styles.logo}
                    src={dealerImage}
                    alt="Dealer logo"
                    onError={this.setFallbackDealerLogo}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        {item && item.inStock && <div className={styles.inStock}>In Stock</div>}
      </a>
    );
  }
}

export default CardVertical;



// WEBPACK FOOTER //
// ./src/Common/CardVertical/index.js